import { EventListeners, MinzeElement, Reactive, Attrs } from 'minze'
import { Font, config } from '../../constants'
import { Callback } from '../../modules/callback'
import { Locale } from '../../modules/locale'
import { AdditionalTypes } from '../../modules/additionalTypes'

Locale.getInstance().registerKeySet('fr', 'lvc-price-type-field', {
  title: 'Type de prix*',
  placeholder: 'Rechercher une priceType',
  choose: 'Choisir',
})

Locale.getInstance().registerKeySet('en', 'lvc-price-type-field', {
  title: 'Price Type*',
  placeholder: 'Search for a priceType',
  choose: 'Choose',
})

export interface LvcPriceTypeField {
  title: string
  placeholder: string
  open: boolean
  onChange: string
  defaultValue: any
  value: string
}

export class LvcPriceTypeField extends MinzeElement {
  private priceTypes: any[] = []
  private selectedId: string = ''
  private filter: string = ''
  attrs: Attrs = ['title', 'placeholder', ['default-value', ''], ['on-change', '']]

  reactive: Reactive = [['value', '']]

  static observedAttributes = ['title', 'on-change']

  async onBlur(e: Event): Promise<void> {
    this.open = false
    this.rerender()
  }

  async onReady() {
    this.priceTypes = config.priceTypes

    if (this.defaultValue && this.defaultValue !== true) {
      this.selectedId = JSON.parse(decodeURIComponent(this.defaultValue)).map((k: any) => k.id)
    }

    this.rerender()
  }

  async onToggle() {
    this.open = !this.open
    this.rerender()
  }

  onPriceTypeClick(e: Event): void {
    let value = (e.target as HTMLElement).getAttribute('data-id')

    if (!value) {
      return
    }

    this.selectedId = value

    this.valueSelected = true

    this.serializeValue()
    this.onToggle()
    this.rerender()
  }

  async onFilterChange(e: Event) {
    this.filter = (e.target as HTMLInputElement).value
    this.rerender()
  }

  serializeValue() {
    let selectedPriceType = this.selectedId
    this.value = encodeURIComponent(JSON.stringify(selectedPriceType))
    Callback.call(this.onChange, selectedPriceType)
    this.cast('change', selectedPriceType)
  }

  getPriceTypesLabel() {
    if (!this.selectedId) {
      return Locale.getKey('lvc-price-type-field.choose')
    }

    const selectedPriceType = this.priceTypes.find(priceType => priceType.id === this.selectedId)

    if (selectedPriceType) {
      return selectedPriceType.name[Locale.language]
    }

    return ''
  }

  renderPriceType = (priceType: any) => {
    if (this.filter && priceType.label.toLowerCase().indexOf(this.filter.toLowerCase()) === -1) {
      return ''
    }

    let isNotSelected = this.selectedId.indexOf(`${priceType.id}`) == -1

    return `
      <button 
        name="priceType"
        class="${isNotSelected ? 'not-selected' : 'selected'}" 
        data-id="${priceType.id}">
        ${priceType.name[Locale.language]}
        ${isNotSelected ? '' : '<lvc-circle-check-select-icon></lvc-circle-check-select-icon>'}
      </button>
    `
  }

  renderPriceTypes = () => {
    let html = ''

    for (let i = 0; i < this.priceTypes.length; i++) {
      html += this.renderPriceType(this.priceTypes[i])
    }

    return html
  }

  html = () => {
    return `
      <div class="title">${this.title || Locale.getKey('lvc-price-type-field.title')}</div>
      <div class="input ${this.selectedId ? 'has-value' : ''}">
        <button class="dropbtn" name="toggle">
          ${this.getPriceTypesLabel()}
          <lvc-chevron-down-icon></lvc-chevron-down-icon>
        </button>
        <div class="overlay ${this.open ? 'show' : ''}" name="overlay"></div>
        <div class="dropdown-content ${this.open ? 'show' : ''}">
          <div class="header">
            <span>${Locale.getKey('lvc-price-type-field.choose')}</span>
            <lvc-chevron-up-icon></lvc-chevron-up-icon>
          </div>
          <div class="priceTypes" data-selected="${this.selectedId}">
            ${this.renderPriceTypes()}
          </div>
        </div> 
      </div>
    `
  }

  css = () => {
    return `
      :host {
        width: 100%;
        min-width: 150px;
      }

      .overlay {
        position: fixed;
        top: 0px;
        left: 0px;
        bottom: 0px;
        right: 0px;
        width: 100%;
        height: 100%;
        z-index: 1;
        display: none;
      }
      .overlay.show {
        display: block;
      }

      .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        height: 48px;
        border: none;
        outline: none;
        padding: 0 16px;
        box-sizing: border-box;
        font-family: ${Font.family};
        font-size: 14px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 150%; /* 21px */
        color: var(--Minuit, #1E007D);
        border-top-left-radius: 24px;
        border-top-right-radius: 24px;
        border-bottom: 1px solid var(--Minuit, #1E007D);
        background: var(--Ciel, #BFF0FF);

      }


      input[name="filter"] {
        width: 100%;
        height: 48px;
        border: none;
        outline: none;
        padding: 0 0;
        box-sizing: border-box;
        font-family: ${Font.family};
        font-size: 14px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 150%; /* 21px */
        color: var(--Minuit, #1E007D);
        background: transparent;
      }

      .title {
        color: var(--Minuit, #1E007D);
        font-variant-numeric: lining-nums tabular-nums;
        font-feature-settings: 'clig' off, 'liga' off;

        /* Body 3 */
        font-family: ${Font.family};
        font-size: 14px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 150%; /* 21px */
        margin-bottom: 3.5px;
      }

      .input {
        display: flex;
        height: 48px;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        flex-shrink: 0;
        align-self: stretch;
        border: 1px solid var(--Minuit, #1E007D);
        position: relative;
        border-radius: 24px;
      }
      
      .input.has-value {
        border: 1px solid rgba(30, 0, 125, 0.3);
        background: #BFF0FF;
      }

      .priceTypes {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        justify-content: stretch;
        overflow: hidden;
        overflow-y: auto;
        max-height: calc(300px - 48px);
        padding: 0 10px;
      }


      .priceTypes button {
        display: flex;
        justify-content: space-between;
        font-family: ${Font.family};
        font-size: 14px;
        line-height: 32px;
        cursor: pointer;
        background-color: transparent;
        color: var(--Minuit, #1E007D);
        text-align: left;
        padding: 8px 8px;
        border: none;
        height: 48px;
        flex: 0 0 48px;
        border-bottom: 1px solid rgba(30, 0, 125, 0.30);
      }

      .priceTypes button:last-child {
        border-bottom: none;
      }

      .priceTypes button.selected {
      }


      /* style.css */

      /* Dropdown Button */
      .dropbtn {
        color: white;
        padding: 12px 16px;
        font-size: 14px;
        font-weight: 500;
        border: none;
        cursor: pointer;
        flex: 1;
        width: 100%;
        background-color: transparent;
        color: var(--Minuit, #1E007D);
        text-align: left;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      /* Dropdown button on hover & focus */
      .dropbtn:hover, .dropbtn:focus {
      }

      /* Dropdown Content */
      .dropdown-content {
        width: calc(100% + 2px);
        margin: -1px;
        display: none;
        position: absolute;
        background-color: #f9f9f9;
        min-width: 160px;
        box-shadow: 0px 8px 16px 0px rgba(0,0,0,0.2);
        z-index: 2;
        top: 0;
        border-radius: 24px; 
        max-height: 300px;
        min-height: 96px;
        border: 1px solid var(--Minuit, #1E007D);
        line-height: 32px;
      }

      .dropdown-content.show {
        display: block;
      }


    `
  }

  eventListeners: EventListeners = [
    ['button[name="toggle"]', 'click', this.onToggle.bind(this)],
    ['button[name="priceType"]', 'click', this.onPriceTypeClick.bind(this)],
    ['input[name="filter"]', 'keyup', this.onFilterChange.bind(this)],
    [this, 'blur', this.onBlur.bind(this)],
    ['div.overlay', 'click', this.onBlur.bind(this)],
  ]
}
